import CollaborationStore from "@/store/collaboration.store";
import { useCallback } from "react";
import { message } from "antd";
import { syncEditRecord, syncModifySpeakerName } from "@/services/record";
import { IEditRecordParams, IMergeRecordParams } from "@/types/record";
import { TranslationOption } from "@/constants/translate";
import { IPG } from "@/types/summary";
import ScrollableContent from "../ScrollableContent";
import { RuningInLink } from '@/constants';

interface SrcContentProps {
  streamName: string;
}

const SrcContent = ({ streamName }: SrcContentProps) => {
  const srcContent = CollaborationStore((state) => state.srcContent);
  const unstableText = CollaborationStore((state) => state.unstableText);
  const unstableTranslateText = CollaborationStore((state) => state.unstableTranslateText);
  const translationOption = CollaborationStore((state) => state.translationOption);

  // 封装编辑记录方法
  const handleEditRecord = useCallback(
    async (params: IEditRecordParams) => {
      const { modifiedParas, deletedParas } = params;
      try {
        await syncEditRecord({
          streamName,
          optEventType: "UPDATE",
          editNote: {
            pg: modifiedParas.map((item) => ({
              ...item,
              roleName: item.speakerName[0],
            })),
          },
        });
        if (deletedParas.length > 0) {
          await syncEditRecord({
            streamName,
            optEventType: "DELETE",
            pis: deletedParas.map((item) => item.pi),
            isTrans: false,
          });
        }
      } catch (error) {
        console.log("handleEditRecord error:", error);
      }
    },
    [streamName]
  );

  // 封装修改发言人
  const handleModifySpeakerName = useCallback(
    async (updatedParas: IPG[], editRoleType: "ONE" | "BATCH") => {
      try {
        await syncModifySpeakerName({
          streamName,
          pis: updatedParas.map((item) => item.pi),
          role: updatedParas[0].role,
          roleName: updatedParas[0].speakerName[0],
          editRoleType,
        });
        if (editRoleType === "BATCH") {
          message.success("发言人设置，应用到全局成功");
        }
      } catch (error) {
        console.log("syncEditRecord error:", error);
      }
    },
    [streamName]
  );

  const handleMergeRecord = async (params: IMergeRecordParams) => {
    const { modifiedParas, deletedParas } = params;
    try {
      if (modifiedParas.length > 0) {
        // 更新段落
        await syncEditRecord({
          streamName,
          optEventType: "UPDATE",
          editNote: {
            pg: modifiedParas.map((item) => ({
              ...item,
              roleName: item.speakerName[0],
            })),
          },
        });
      }
      if (deletedParas.length > 0) {
        // 删除段落
        await syncEditRecord({
          streamName,
          optEventType: "DELETE",
          pis: deletedParas.map((item) => item.pi),
          isTrans: false,
        });
      }
    } catch (error) {
      console.log("handleMergeRecord error:", error);
    }
  };

  const handleSpeakerPopoverChange = useCallback((visible: boolean) => {
    // 发言人弹窗状态变化处理
    void visible; // 防止linter警告
    // 这里可以根据需要添加额外的逻辑
  }, []);

  // 渲染原文内容
  const renderContent = useCallback((isTrans: boolean) => {
    return (
      <ScrollableContent
        data={srcContent}
        unstableText={isTrans ? unstableTranslateText : unstableText}
        isTrans={isTrans}
        onEditRecord={handleEditRecord}
        onModifySpeakerName={handleModifySpeakerName}
        onMergeRecord={handleMergeRecord}
        onSpeakerPopoverChange={handleSpeakerPopoverChange}
        className={`flex-1 ${translationOption === TranslationOption.both ? isTrans ? "h-[calc(50%-24px)] pt-6" : "h-[calc(50%-24px)] pb-6 pt-0.25" : "h-[calc(100%-48px)]"
          }`}
      />
    );
  }, [srcContent, unstableText, translationOption, handleEditRecord, handleModifySpeakerName, handleMergeRecord, handleSpeakerPopoverChange]);

  return (
    <div className={`flex-1 h-full overflow-hidden flex flex-col ${RuningInLink ? '' : 'max-w-full mx-4 sm:mx-8 md:mx-16 lg:mx-32 xl:mx-60'}`}>
      {/* 原文区域 */}
      {translationOption !== TranslationOption.translation && renderContent(false)}

      {/* 分割线 */}
      {translationOption === TranslationOption.both && (
        <div
          className="mx-2"
          style={{
            height: '1px',
            background: 'linear-gradient(90deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.1) 15%, rgba(0, 0, 0, 0.1) 85%, rgba(0, 0, 0, 0) 100%)'
          }}
        />
      )}

      {/* 译文区域 */}
      {translationOption !== TranslationOption.original && renderContent(true)}
      <div className='h-6'></div>
    </div>
  );
};

export default SrcContent;
